import { createRouter, createWebHistory } from "vue-router";
import { hasEmployeePageAccessQuery } from "~/graphql/queries/hasEmployeePageAccess";
import { hasManageWorkSchedulesAccessQuery } from "~/graphql/queries/hasManageWorkSchedulesAccess";
import { hasStudentPageAccessQuery } from "~/graphql/queries/hasStudentPageAccess";
import { hasTimeRegistrationApprovalAccessQuery } from "~/graphql/queries/hasTimeRegistrationApprovalAccess";
import { store } from "~/store";
import { beginNavigation, endNavigation, setNotFound } from "~/utils/notFound";
import { notFoundForFailedAccessQuery } from "~/utils/routerUtils";
import { toast } from "~/utils/toast";
import PassThrough from "~/views/PassThrough";
import { apolloClient } from "~/vue-apollo";

// True when the user has been silently signed in but their JWT shows
// no institution that allows local (username/password) login —
// ENG-2765. Anonymous visitors get the form as usual.
//
// During rollout an older JWT may not carry `allowLocalLogin` — treat
// a missing flag as "allowed" (the backend default) so we don't
// redirect users away from `/l` post-deploy with a stale cached token.
function shouldDenyLocalLogin() {
  const tokenData = store.getters["session/tokenData"];
  if (!tokenData) return false;
  const institutions = tokenData.fguInstitutions || [];
  if (institutions.length === 0) return false;
  return !institutions.some((institution) => institution.allowLocalLogin ?? true);
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      name: "Login",
      path: "/",
      component: () => import("~/views/Login"),
    },
    {
      name: "LocalLogin",
      path: "/l",
      component: () => import("~/views/LocalLogin"),
      beforeEnter: (_to, _from, next) => {
        if (shouldDenyLocalLogin()) {
          next({ name: "LocalLoginNotAllowed" });
        } else {
          next();
        }
      },
    },
    {
      name: "LocalLoginNotAllowed",
      path: "/local-login-not-allowed",
      component: () => import("~/views/LocalLoginNotAllowed"),
    },
    {
      name: "PasswordReset",
      path: "/password-reset/:passwordResetToken",
      component: () => import("~/views/PasswordReset"),
    },
    {
      path: "/:fguInstitutionNumber",
      component: () => import("~/components/auth/LogInBarrier"),
      meta: { breadcrumb: true },
      beforeEnter: (to, _from, next) => {
        if (!to.name || to.params.fguInstitutionNumber.length !== 6 || isNaN(to.params.fguInstitutionNumber)) {
          setNotFound(to);
        }

        next();
      },
      children: [
        {
          path: "",
          component: () => import("~/layouts/Layout1"),
          children: [
            {
              path: "change-password",
              name: "ChangePassword",
              component: () => import("~/components/profile/ChangePassword"),
            },
            {
              path: "two-factor",
              name: "TwoFactorSettings",
              component: () => import("~/components/profile/TwoFactorSettings"),
            },
            {
              path: "home",
              name: "Homepage",
              component: () => import("~/views/Homepage"),
            },
            {
              path: "reports",
              component: PassThrough,
              children: [
                {
                  path: "",
                  name: "ReportsIndex",
                  component: () => import("~/views/ReportsIndex"),
                },
                {
                  path: "list-extract",
                  name: "ListExtractReport",
                  component: () => import("~/views/ListExtractReport"),
                },
                {
                  path: "reduced-stay",
                  name: "ReducedStayReport",
                  component: () => import("~/views/ReducedStayReport"),
                },
                {
                  path: "reduced-time",
                  name: "ReducedTimeReport",
                  component: () => import("~/views/ReducedTimeReport"),
                },
                {
                  path: "stay-consents",
                  name: "StayConsentReport",
                  component: () => import("~/views/StayConsentReport"),
                },
                {
                  path: "tax-job",
                  name: "TaxJobReport",
                  component: () => import("~/views/TaxJobReport"),
                },
                {
                  path: "time-report",
                  name: "TimeReport",
                  component: () => import("~/views/TimeReport"),
                },
                {
                  path: "cancelled-events-report",
                  name: "CancelledEventsReport",
                  component: () => import("~/views/CancelledEventsReport"),
                },
                {
                  path: "cosa-reports",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "CosaReports",
                      component: () => import("~/views/CosaReportsIndex"),
                    },

                    {
                      path: ":reportId",
                      name: "CosaReportPage",
                      component: () => import("~/views/CosaReportPage"),
                    },
                  ],
                },
                {
                  path: "count-periods",
                  name: "CountPeriodReport",
                  component: () => import("~/views/CountPeriodReport"),
                },
                {
                  path: "pool-extensions",
                  name: "PoolExtensionReport",
                  component: () => import("~/views/PoolExtensionReport"),
                },
                {
                  path: "time-registrations",
                  name: "TimeRegistrationsReport",
                  component: () => import("~/views/TimeRegistrationsReport"),
                },
                {
                  path: "employee-absence-report",
                  name: "EmployeeAbsenceReport",
                  component: () => import("~/views/EmployeeAbsenceReport"),
                },
                {
                  path: "short-messages",
                  name: "ShortMessagesReport",
                  component: () => import("~/views/ShortMessagesReport"),
                },
                {
                  path: "exam-excluded-activities",
                  name: "ExamExcludedActivitiesReport",
                  component: () => import("~/views/ExamExcludedActivitiesReport"),
                },
                {
                  path: "absence-overview",
                  name: "AbsenceOverview",
                  component: () => import("~/views/AbsenceOverview"),
                },
              ],
            },

            {
              path: "education-modules",
              redirect: (to) => ({
                name: "EducationModules",
                params: { ...to.params, type: "workshops" },
              }),
            },

            {
              path: "education-modules",
              component: PassThrough,
              children: [
                {
                  path: ":type",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "EducationModules",
                      component: () => import("~/views/EducationModulesIndex"),
                    },
                    {
                      path: ":educationModuleId",
                      component: PassThrough,
                      children: [
                        {
                          path: "attendances",
                          name: "EducationModule",
                          component: () => import("~/views/EducationModule"),
                          children: [
                            {
                              path: "overview",
                              name: "EducationModuleAttendanceOverview",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleAttendanceOverview"),
                            },
                            {
                              path: "absence",
                              name: "EducationModuleAttendanceAbsence",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleAttendanceAbsence"),
                            },
                            {
                              path: "competences",
                              name: "EducationModuleAttendanceCompetenceTable",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleCompetenceTable"),
                            },
                            {
                              path: "teachers",
                              name: "EducationModuleTeachers",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleUsers"),
                              props: { userRole: "teacher" },
                            },
                            {
                              path: "examiners",
                              name: "EducationModuleExaminers",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleUsers"),
                              props: { userRole: "examiner" },
                            },
                            {
                              path: "schedule",
                              name: "EducationModuleSchedule",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleSchedule"),
                            },
                            {
                              path: "registration-errors",
                              name: "EducationModuleRegistrationErrors",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/EducationModuleRegistrationErrors"),
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },

            {
              path: "notifications",
              name: "Notifications",
              component: () => import("~/views/Notifications"),
            },

            {
              path: "notifications/feed",
              name: "NotificationFeed",
              component: () => import("~/views/NotificationFeed"),
            },

            {
              path: "search",
              name: "SearchResults",
              component: () => import("~/views/SearchResults"),
            },

            {
              path: "settings",
              component: PassThrough,
              children: [
                {
                  path: "",
                  name: "SettingsIndex",
                  component: () => import("~/views/SettingsIndex"),
                },
                {
                  path: "pay-deductions",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "PayDeductionsIndex",
                      component: () => import("~/views/PayDeductionsIndex"),
                    },
                    {
                      path: ":id",
                      name: "PayDeduction",
                      component: () => import("~/views/PayDeduction"),
                    },
                  ],
                },
                {
                  path: "screenings",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "ScreeningsIndex",
                      component: () => import("~/views/ScreeningsIndex"),
                    },
                  ],
                },
                {
                  path: "bi-tokens",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "BiTokensIndex",
                      component: () => import("~/views/BiTokensIndex"),
                    },
                  ],
                },
                {
                  path: "ip-whitelist",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "IpWhitelistIndex",
                      component: () => import("~/views/IpWhitelistIndex"),
                    },
                  ],
                },
                {
                  path: "agreements",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "AgreementsIndex",
                      component: () => import("~/views/AgreementsIndex"),
                    },
                  ],
                },
                {
                  path: "exam-periods",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "ExamPeriodsIndex",
                      component: () => import("~/views/ExamPeriodsIndex"),
                    },
                    {
                      path: ":examPeriodId",
                      component: () => import("~/views/ExamPeriod"),
                      children: [
                        {
                          path: "",
                          name: "ExamPeriodExamTeams",
                          component: () => import("~/views/ExamPeriodExamTeams"),
                        },
                        {
                          path: "registrations",
                          name: "ExamPeriodExamRegistrations",
                          meta: { breadcrumb: false },
                          component: () => import("~/views/ExamPeriodExamRegistrations"),
                        },
                        {
                          path: "low-level-students",
                          name: "ExamPeriodLowLevelStudents",
                          meta: { breadcrumb: false },
                          component: () => import("~/views/ExamPeriodLowLevelStudents"),
                        },
                        {
                          path: "missing-order-list",
                          name: "ExamPeriodMissingOrderList",
                          meta: { breadcrumb: false },
                          component: () => import("~/views/ExamPeriodMissingOrderList"),
                        },
                        {
                          name: "ExamTeam",
                          path: "exam-teams/:examTeamId",
                          component: () => import("~/views/ExamTeam"),
                          children: [
                            {
                              name: "ExamRegistrations",
                              path: "registrations",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/ExamRegistrations"),
                            },
                            {
                              name: "ExamRegistrationsDeregistered",
                              path: "registrations-deregistered",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/ExamRegistrationsDeregistered"),
                            },
                            {
                              name: "ExamOrderLists",
                              path: "order-lists",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/ExamOrderLists"),
                            },
                            {
                              name: "ExamGradingLists",
                              path: "grading-lists",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/ExamGradingLists"),
                            },
                            {
                              name: "NotDrawnStudents",
                              path: "not-drawn-students",
                              meta: { breadcrumb: false },
                              component: () => import("~/views/NotDrawnStudents"),
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "locations",
                  name: "LocationsIndex",
                  component: () => import("~/views/LocationsIndex"),
                },
                {
                  path: "ringtime-schedules",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "RingtimeSchedulesIndex",
                      component: () => import("~/views/RingtimeSchedulesIndex"),
                    },
                    {
                      path: ":ringtimeScheduleId",
                      name: "RingtimeSlotsIndex",
                      component: () => import("~/views/RingtimeSlotsIndex"),
                    },
                  ],
                },
                {
                  path: "users",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "UsersIndex",
                      component: () => import("~/views/UsersIndex"),
                    },
                    {
                      path: "user-groups",
                      name: "UserGroupsIndex",
                      component: () => import("~/views/UserGroupsIndex"),
                    },
                    {
                      path: "user-teams",
                      name: "UserTeamsIndex",
                      component: () => import("~/views/UserTeamsIndex"),
                    },
                    {
                      path: ":userId",
                      component: PassThrough,
                      children: [
                        {
                          path: "",
                          name: "UserEmployeePage",
                          component: () => import("~/views/UserPage"),
                          async beforeEnter(to) {
                            try {
                              const response = await apolloClient.query({
                                query: hasEmployeePageAccessQuery,
                                variables: {
                                  id: to.params.userId,
                                },
                                fetchPolicy: "no-cache",
                              });

                              if (response.data && !response.data.user.hasEmployeePageAccess) {
                                setNotFound(to);
                                return true;
                              }
                            } catch (error) {
                              return notFoundForFailedAccessQuery(error, to);
                            }
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "consents",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "ConsentsIndex",
                      component: () => import("~/views/ConsentsIndex"),
                    },
                  ],
                },
                {
                  path: "wage-periods",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "WagePeriodsIndex",
                      component: () => import("~/views/WagePeriodsIndex"),
                    },
                    {
                      path: ":wagePeriodId",
                      component: PassThrough,
                      children: [
                        {
                          name: "WagePeriodStayWagePeriods",
                          path: "stay-wage-periods",
                          meta: { breadcrumb: true },
                          component: () => import("~/views/WagePeriodStayWagePeriods"),
                        },
                        {
                          name: "MissingRegistrationsInWagePeriod",
                          path: "missing-registrations-in-wage-period",
                          meta: { breadcrumb: true },
                          component: () => import("~/views/MissingRegistrationsInWagePeriod"),
                        },
                        {
                          path: "pay-runs",
                          component: PassThrough,
                          children: [
                            {
                              path: "",
                              name: "PayRunsIndex",
                              meta: { breadcrumb: true },
                              component: () => import("~/views/PayRunsIndex"),
                            },
                            {
                              path: ":payRunId",
                              name: PassThrough,
                              meta: { breadcrumb: false },
                              component: () => import("~/views/PayRunPage"),
                              children: [
                                {
                                  name: "RevenueReportPage",
                                  path: "revenue-report",
                                  meta: { breadcrumb: true },
                                  component: () => import("~/views/RevenueReportPage"),
                                },
                                {
                                  name: "PayChecksPage",
                                  path: "pay-checks",
                                  meta: { breadcrumb: true },
                                  component: () => import("~/views/PayChecksPage"),
                                },
                                {
                                  name: "PayStatementDrafts",
                                  path: "pay-statement-drafts",
                                  meta: { breadcrumb: false },
                                  component: () => import("~/views/PayStatementDrafts"),
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },

            {
              path: "students",
              component: PassThrough,
              name: "Students",
              meta: { title: "Elever" },
              redirect: { name: "StaysIndex" },
              children: [
                {
                  path: "active",
                  name: "StaysIndex",
                  component: () => import("~/views/StayIndex"),
                },

                {
                  path: "ended",
                  name: "EndedStaysIndex",
                  component: () => import("~/views/EndedStaysIndex"),
                },

                {
                  path: "upcoming",
                  name: "UpcomingStaysIndex",
                  component: () => import("~/views/UpcomingStaysIndex"),
                },

                {
                  path: ":studentId",
                  component: PassThrough,
                  async beforeEnter(to, from) {
                    try {
                      const response = await apolloClient.query({
                        query: hasStudentPageAccessQuery,
                        variables: {
                          id: to.params.studentId,
                        },
                        fetchPolicy: "no-cache",
                      });

                      if (response.data && !response.data.student.hasStudentPageAccess) {
                        toast.error("Ingen adgang", "Du har ikke adgang til denne elev");

                        if (from.name === undefined) {
                          return {
                            name: "StaysIndex",
                          };
                        } else {
                          return false;
                        }
                      }
                    } catch (error) {
                      return notFoundForFailedAccessQuery(error, to);
                    }
                  },
                  children: [
                    {
                      path: "",
                      name: "ClassicStudentPage",
                      component: () => import("~/views/ClassicStudentPage"),
                      alias: "classic",
                    },
                    {
                      path: "schedule",
                      name: "StudentSchedule",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/StudentSchedule"),
                    },
                    {
                      path: "overview",
                      name: "StudentOverview",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/StudentOverview"),
                    },
                    {
                      path: "egu-overview",
                      name: "StudentEguOverview",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/StudentEguOverview"),
                    },
                  ],
                },
              ],
            },

            {
              path: "teams",
              component: PassThrough,
              children: [
                {
                  path: "",
                  name: "Teams",
                  component: () => import("~/views/TeamsIndex"),
                },
                {
                  path: ":teamId",
                  component: () => import("~/views/TeamOverview"),
                  children: [
                    {
                      path: "",
                      name: "TeamEditPage",
                      component: () => import("~/views/TeamStaysList"),
                    },
                    {
                      path: "absence",
                      name: "TeamAbsence",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/TeamAbsence"),
                    },
                    {
                      path: "schedule",
                      name: "TeamSchedule",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/TeamSchedule"),
                    },
                    {
                      path: "competence-tables",
                      name: "TeamCompetenceTables",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/TeamCompetenceTables"),
                    },
                    {
                      path: "registration-errors",
                      name: "TeamRegistrationErrors",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/TeamRegistrationErrors"),
                    },
                  ],
                },
              ],
            },

            {
              path: "time-tables",
              name: "TimeTablesIndex",
              component: () => import("~/views/TimeTableIndex"),
            },

            {
              path: "trainings",
              name: "TrainingsUpcoming",
              component: () => import("~/views/TrainingsUpcoming"),
            },

            {
              path: "tools",
              component: PassThrough,
              children: [
                {
                  path: "",
                  name: "ToolsIndex",
                  component: () => import("~/views/ToolsIndex"),
                },
                {
                  path: "users-overview",
                  name: "UsersOverview",
                  component: () => import("~/views/UsersOverview"),
                },
                {
                  path: "egu-elever",
                  name: "EguBoard",
                  component: () => import("~/views/EguBoard"),
                },
                {
                  path: "fgu-schools",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "FguSchoolsIndex",
                      component: () => import("~/views/FguSchoolsIndex"),
                    },
                    {
                      path: ":fguSchoolId",
                      name: "FguSchoolPage",
                      component: () => import("~/views/FguSchoolPage"),
                    },
                  ],
                },
                {
                  path: "missing-attendance-levels",
                  name: "MissingAttendanceLevelsPage",
                  component: () => import("~/views/MissingAttendanceLevelsPage"),
                },
                {
                  path: "employee-projects",
                  name: "EmployeeProjectsPage",
                  component: () => import("~/views/EmployeeProjectsPage"),
                },
                {
                  path: "screenings-overview",
                  name: "ScreeningsReportPage",
                  component: () => import("~/views/ScreeningsReportPage"),
                },
                {
                  path: "mentors",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "MentorsIndex",
                      component: () => import("~/views/MentorsIndex"),
                    },
                    {
                      path: ":id",
                      name: "MentorPage",
                      component: () => import("~/views/MentorPage"),
                    },
                  ],
                },
                {
                  path: "eboks",
                  name: "EboksIndex",
                  component: () => import("~/views/EboksIndex"),
                  children: [
                    {
                      path: "",
                      name: "EboksSendMass",
                      meta: { breadcrumb: false },
                      component: () => import("~/views/EboksSendMass"),
                    },
                    {
                      path: "certificates",
                      name: "EboksSendCertificates",
                      component: () => import("~/views/EboksSendCertificates"),
                    },
                    {
                      path: "sent-report",
                      name: "EboksSentReport",
                      component: () => import("~/views/EboksSentReport"),
                    },
                    {
                      path: "digital-post",
                      name: "DigitalPostStatus",
                      component: PassThrough,
                      // Breadcrumbs and the subnav link to this name; vue-router renders no empty-path child for a parent name.
                      redirect: { name: "DigitalPostShipments" },
                      meta: { title: "e-Boks: Status for afsendelser (Digital Post)" },
                      children: [
                        {
                          path: "",
                          name: "DigitalPostShipments",
                          meta: { breadcrumb: false },
                          component: () => import("~/views/DigitalPostShipments"),
                        },
                        {
                          path: ":id",
                          name: "DigitalPostShipmentPage",
                          component: () => import("~/views/DigitalPostShipmentPage"),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "companies",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "CompaniesIndex",
                      component: () => import("~/views/CompaniesIndex"),
                    },
                    {
                      path: ":companyId",
                      name: "CompanyPage",
                      component: () => import("~/views/CompanyPage"),
                    },
                  ],
                },
                {
                  path: "imported-details-lines",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "ImportedDetailsLinesIndex",
                      component: () => import("~/views/ImportedDetailsLinesIndex"),
                    },
                  ],
                },
                {
                  path: "aub",
                  name: "AubTool",
                  component: () => import("~/views/AubTool"),
                },
                {
                  path: "trainings",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "TrainingsIndex",
                      component: () => import("~/views/TrainingsIndex"),
                    },
                    {
                      path: ":trainingId",
                      name: "TrainingPage",
                      component: () => import("~/views/TrainingPage"),
                    },
                  ],
                },
                {
                  path: "exam-registration-events",
                  name: "ExamRegistrationEvents",
                  component: () => import("~/views/ExamRegistrationEventsLog"),
                },
                {
                  path: "competence-tables",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "CompetenceTablesIndex",
                      component: () => import("~/views/CompetenceTablesIndex"),
                    },
                    {
                      path: ":id",
                      name: "CompetenceTable",
                      component: () => import("~/views/CompetenceTable"),
                    },
                  ],
                },
                {
                  path: "questionnaires",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "QuestionnairesIndex",
                      component: () => import("~/views/QuestionnairesIndex"),
                    },
                    {
                      path: "new",
                      name: "QuestionnaireNew",
                      component: () => import("~/views/QuestionnaireForm"),
                    },
                    {
                      path: ":id",
                      name: "QuestionnaireEdit",
                      component: () => import("~/views/QuestionnaireForm"),
                    },
                  ],
                },
                {
                  path: "kui-contacts",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "KuiContactsIndex",
                      component: () => import("~/views/KuiContactsIndex"),
                    },
                    {
                      path: ":kuiContactId",
                      name: "KuiContactPage",
                      component: () => import("~/views/KuiContactPage"),
                    },
                  ],
                },
                {
                  path: "schedule-plans",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "SchedulePlansIndex",
                      component: () => import("~/views/SchedulePlansIndex"),
                    },
                    {
                      path: ":schedulePlanId",
                      name: "SchedulePlanShow",
                      component: () => import("~/views/SchedulePlanShow"),
                    },
                  ],
                },
                {
                  path: "school-day-calendars",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "SchoolDayCalendarIndex",
                      component: () => import("~/views/SchoolDayCalendarIndex"),
                    },
                    {
                      path: ":schoolDayCalendarId",
                      name: "SchoolDayCalendar",
                      component: () => import("~/views/SchoolDayCalendar"),
                    },
                  ],
                },
                {
                  path: "schools",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "SchoolsIndex",
                      component: () => import("~/views/SchoolsIndex"),
                    },
                    {
                      path: ":schoolId",
                      name: "SchoolPage",
                      component: () => import("~/views/SchoolPage"),
                    },
                  ],
                },
                {
                  path: "stay-taxonomy-activities",
                  name: "StayTaxonomyActivities",
                  component: () => import("~/views/StayTaxonomyActivities"),
                },
                {
                  path: "taxonomy-statuses",
                  component: PassThrough,
                  children: [
                    {
                      path: "",
                      name: "TaxonomyStatusesIndex",
                      component: () => import("~/views/TaxonomyStatusesIndex"),
                    },
                    {
                      path: ":taxonomyStatusId",
                      name: "TaxonomyStatusPage",
                      component: () => import("~/views/TaxonomyStatusPage"),
                    },
                  ],
                },
                {
                  path: "approve-time-registrations",
                  name: "ApproveTimeRegistrationsIndex",
                  component: () => import("~/views/ApproveTimeRegistrationsIndex"),
                  async beforeEnter(to) {
                    try {
                      const response = await apolloClient.query({
                        query: hasTimeRegistrationApprovalAccessQuery,
                        fetchPolicy: "no-cache",
                      });

                      if (response.data && !response.data.hasTimeRegistrationApprovalAccess) {
                        setNotFound(to);
                        return true;
                      }
                    } catch (error) {
                      return notFoundForFailedAccessQuery(error, to);
                    }
                  },
                },
                {
                  path: "manage-work-schedules",
                  name: "WorkScheduleIndex",
                  component: () => import("~/views/WorkScheduleIndex"),
                  async beforeEnter(to) {
                    try {
                      const response = await apolloClient.query({
                        query: hasManageWorkSchedulesAccessQuery,
                        fetchPolicy: "no-cache",
                      });

                      if (response.data && !response.data.hasManageWorkSchedulesAccess) {
                        setNotFound(to);
                        return true;
                      }
                    } catch (error) {
                      return notFoundForFailedAccessQuery(error, to);
                    }
                  },
                },
              ],
            },

            {
              path: "optageliste",
              component: PassThrough,
              children: [
                {
                  path: "",
                  name: "PlannedStaysIndex",
                  component: () => import("~/views/PlannedStaysIndex"),
                },
                {
                  path: ":plannedStayId",
                  name: "PlannedStayShow",
                  component: () => import("~/views/PlannedStayShow"),
                },
              ],
            },

            {
              path: "time-registrations",
              name: "TimeRegistrationsIndex",
              component: () => import("~/views/TimeRegistrationsIndex"),
            },
          ],
        },
      ],
    },
    {
      // Must stay nameless: PageHeader builds its breadcrumbs by resolving each
      // matched route record, and vue-router resolves a record by its raw path
      // pattern. Naming this route would make every pattern that fails to
      // resolve land here, collapsing the breadcrumbs into a single 404 crumb
      // instead of being dropped for having no name.
      path: "/:pathMatch(.*)*",
      component: () => import("~/views/NotFound"),
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        top: 152,
      };
    }

    if (savedPosition) {
      return { left: savedPosition.left, top: savedPosition.top + 24 };
    }

    return { left: 0, top: 0 };
  },
});

// A 404 is rendered in place, so nothing navigates away from it — these two
// hooks are what let the next page render. `beforeEach` runs before the guards
// that set the flag, and `afterEach` keeps it only when the navigation that
// asked for it is the one that landed.
router.beforeEach((to) => {
  beginNavigation(to);
});

router.afterEach((to, from, failure) => {
  endNavigation(to, from, failure);
});
